import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  Container,
  Link
} from '@mui/material';
import {
  Facebook,
  Instagram,
  Twitter,
  LinkedIn,
  LocationOn,
  Phone,
  Email
} from '@mui/icons-material';
import { useSelector } from 'react-redux';

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { address, email, phone } = useSelector((state) => state.themeValue?.contact || {});
  const social = useSelector((state) => state.themeValue?.social || {});
  const brand = useSelector((state) => state.themeValue?.colors) || {};
  const start = brand.primary_gradient_start_color || '#FF2D95';
  const end = brand.primary_gradient_end_color || '#7B1FA2';

  const socialLinks = useMemo(
    () =>
      [
        { key: 'facebook', icon: <Facebook />, color: '#1877F2', url: social.facebook },
        { key: 'instagram', icon: <Instagram />, color: '#E4405F', url: social.instagram },
        { key: 'twitter', icon: <Twitter />, color: '#1DA1F2', url: social.twitter },
        { key: 'linkedin', icon: <LinkedIn />, color: '#0A66C2', url: social.linkedin },
      ].filter((item) => typeof item.url === 'string' && item.url.trim()),
    [social]
  );

  return (
    <Box
      sx={{
        background: `linear-gradient(180deg, #FBF8FC 0%, #F3EAF7 100%)`,
        color: brand.text || '#1A1220',
        pt: 8,
        pb: 4,
        marginTop: 3,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid size={{ xs: 6, sm: 6, md: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: end }}>
              Quick Links
            </Typography>
            <Stack spacing={1.2}>
              {[
                { name: 'Home', path: '/' },
                { name: 'About Us', path: '/about' },
                { name: 'Contact Us', path: '/contact' },
                { name: 'Privacy Policy', path: '/privacy' },
                { name: 'Terms & Conditions', path: '/terms-and-condition' },
                { name: 'Disclaimer', path: '/disclaimer' },
              ].map((item, index) => (
                <Link
                  key={index}
                  href={item.path}
                  underline="none"
                  sx={{
                    color: brand.text || '#333',
                    '&:hover': {
                      color: start,
                      transform: 'translateX(4px)',
                    },
                    transition: 'all 0.2s ease',
                    width: 'fit-content',
                  }}
                >
                  {item.name}
                </Link>
              ))}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: end }}>
              Contact Us
            </Typography>
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              {address && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <LocationOn sx={{ mr: 1.5, mt: '2px', fontSize: '1.2rem', color: start }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: brand.text || '#333' }}>
                    {address}
                  </Typography>
                </Box>
              )}
              {phone && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Phone sx={{ mr: 1.5, fontSize: '1.2rem', color: start }} />
                  <Typography
                    variant="body2"
                    component="a"
                    href={`tel:${phone}`}
                    sx={{ textDecoration: 'none', color: brand.text || '#333', '&:hover': { color: start } }}
                  >
                    {phone}
                  </Typography>
                </Box>
              )}
              {email && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Email sx={{ mr: 1.5, fontSize: '1.2rem', color: start }} />
                  <Typography
                    variant="body2"
                    component="a"
                    href={`mailto:${email}`}
                    sx={{ textDecoration: 'none', color: brand.text || '#333', '&:hover': { color: start } }}
                  >
                    {email}
                  </Typography>
                </Box>
              )}
            </Stack>

            {socialLinks.length > 0 && (
              <Stack direction="row" spacing={2}>
                {socialLinks.map((item) => (
                  <IconButton
                    key={item.key}
                    onClick={() => window.open(item.url, '_blank', 'noopener,noreferrer')}
                    aria-label={item.key}
                    sx={{
                      backgroundColor: '#fff',
                      color: end,
                      border: `1px solid ${end}33`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${start}, ${end})`,
                        color: '#fff',
                        borderColor: 'transparent',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                      width: 40,
                      height: 40,
                    }}
                  >
                    {item.icon}
                  </IconButton>
                ))}
              </Stack>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: `${end}33` }} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: isMobile ? 'center' : 'left',
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            © {new Date().getFullYear()} E and J Global Realtors. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
